import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
  Modal,
  IconButton,
  Divider,
  Button,
} from "@mui/material";

import { CloseSelectServiceBannerModalButtonIcon } from "../../assets/icons/create-service-icons";
import { Banner, CreateServiceFormData } from "../../types/service.type";

interface SelectServiceBannerImageModalProps {
  open: boolean;
  closeModal: () => void;
  banners?: Banner[];
  isLoadingBanners: boolean;
}

const SelectServiceBannerImageModal = ({
  open,
  closeModal,
  banners,
  isLoadingBanners,
}: SelectServiceBannerImageModalProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.serviceInformationStep" });
  const { control, setValue, trigger } = useFormContext<CreateServiceFormData>();
  const [selectedBannerId, setSelectedBannerId] = useState<string>("");

  const handleSelect = async () => {
    if (selectedBannerId) {
      setValue("banner", Number(selectedBannerId));
      await trigger(["banner"]);
      closeModal();
    }
  };

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.serviceBannerModalContainer}>
        <Box sx={styles.serviceBannerModalHeader}>
          <Typography sx={styles.serviceBannerModalHeaderTitle}>{t("chooseBanner")}</Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseSelectServiceBannerModalButtonIcon />
          </IconButton>
        </Box>

        <Divider flexItem orientation="horizontal" />

        {isLoadingBanners ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Controller
            name="banner"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                value={selectedBannerId}
                onChange={(e) => setSelectedBannerId(e.target.value)}
                sx={styles.radioImageGroup}
              >
                {banners?.map((banner) => (
                  <FormControlLabel
                    key={banner.id}
                    value={banner.id.toString()}
                    control={<Radio size="small" sx={styles.radioGroupRadio} />}
                    label={<Box component="img" src={banner.url} sx={styles.radioImage} />}
                    sx={styles.radioImageFormControlLabel}
                  />
                ))}
              </RadioGroup>
            )}
          />
        )}

        <Divider flexItem orientation="horizontal" />

        <Box sx={styles.serviceBannerModalFooter}>
          <Button variant="outlined-md" onClick={closeModal}>
            {t("cancel")}
          </Button>
          <Button variant="primary-md" onClick={handleSelect} disabled={!selectedBannerId}>
            {t("add")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectServiceBannerImageModal;

const styles = {
  serviceBannerModalContainer: {
    width: { xs: "90%", md: "640px" },
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#FFF",
    borderRadius: "12px",
    overflow: "auto",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  serviceBannerModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 24px 0 24px",
  },
  serviceBannerModalHeaderTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },

  radioImageGroup: {
    padding: "0 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "relative",
  },
  radioGroupRadio: { position: "absolute", top: 0, right: 0 },
  radioImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  radioImageFormControlLabel: {
    position: "relative",
    width: "100%",
    height: "100px",
    margin: 0,
    border: "3px solid transparent",
    overflow: "hidden",
    borderRadius: "12px",
    transition: "all 0.2s ease-in-out",
    display: "flex",
    "& .MuiFormControlLabel-label": {
      flex: 1,
      height: "100%",
    },
    "&:has(.Mui-checked)": {
      border: "3px solid var(--Brand-600, #008B07)",
      zIndex: 1,
    },
    "&:hover": {
      borderColor: "var(--Gray-200, #E4E7EC)",
    },
  },
  serviceBannerModalFooter: {
    padding: "0 24px 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
};
